/**
 * Classification Rules Index Page
 *
 * Main page for managing classification rules.
 * Shows all rules and allows users to create, edit, and delete them.
 *
 * Features:
 * - List of classification rules with cards
 * - Create/edit modal with pattern testing
 * - Active/inactive toggle
 * - Delete with confirmation
 * - Toast notifications for all operations
 * - Loading skeleton during data fetch
 * - Empty state when no rules
 * - Link back to transactions page
 */

import { useEffect, useCallback, useState } from 'react';
import { Head, Link, usePage } from '@inertiajs/react';
import { Toaster, toast } from 'sonner';
import { ListChecks, ArrowLeft, Loader2, Plus, AlertTriangle, CheckSquare, Square } from 'lucide-react';
import { Navbar } from '@/components';

import type { ClassificationRulesPageProps, ClassificationRule, RulePreviewTransaction, RulePreviewRule, BulkApplyScope } from '@/types/accounting';
import { useClassificationRules } from '@/hooks/useClassificationRules';
import { ClassificationRuleCard } from './components/ClassificationRuleCard';
import { RuleFormModal } from './components/RuleFormModal';
import { RulePreviewModal } from './components/RulePreviewModal';
import { BulkApplyBar } from './components/BulkApplyBar';

/**
 * Delete confirmation modal state
 */
interface DeleteConfirmState {
  isOpen: boolean;
  ruleId: number | null;
  ruleName: string;
}

/**
 * ClassificationRulesIndex is the main page for managing classification rules.
 */
export default function ClassificationRulesIndex() {
  // Get page props from Inertia (accounts for the form)
  const { accounts, accountTree } = usePage<ClassificationRulesPageProps>().props;

  // Hook for rule management
  const {
    rules,
    loading,
    error,
    fetchRules,
    createRule,
    updateRule,
    deleteRule,
    testPattern,
    previewRule: fetchPreviewData,
    applyRule: applyRuleApi,
    bulkApplyRules,
    clearError,
  } = useClassificationRules();

  // Form modal state
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingRule, setEditingRule] = useState<ClassificationRule | null>(null);

  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState<DeleteConfirmState>({
    isOpen: false,
    ruleId: null,
    ruleName: '',
  });
  const [isDeleting, setIsDeleting] = useState(false);

  // Preview modal state
  const [previewRule, setPreviewRule] = useState<ClassificationRule | null>(null);
  const [previewData, setPreviewData] = useState<{
    transactions: RulePreviewTransaction[];
    rule: RulePreviewRule;
  } | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);

  // Selection state for multi-rule selection
  const [selectedRuleIds, setSelectedRuleIds] = useState<Set<number>>(new Set());
  const [isBulkApplying, setIsBulkApplying] = useState(false);

  // Fetch rules on mount
  useEffect(() => {
    fetchRules().catch(() => {
      // Error is handled by the hook
    });
  }, [fetchRules]);

  // Handle create button click
  const handleCreateClick = useCallback(() => {
    setEditingRule(null);
    setShowFormModal(true);
  }, []);

  // Handle edit button click
  const handleEditClick = useCallback((rule: ClassificationRule) => {
    setEditingRule(rule);
    setShowFormModal(true);
  }, []);

  // Handle modal close
  const handleModalClose = useCallback(() => {
    setShowFormModal(false);
    setEditingRule(null);
  }, []);

  // Handle create rule
  const handleCreate = useCallback(
    async (data: Parameters<typeof createRule>[0]) => {
      try {
        await createRule(data);
        toast.success(`Rule "${data.name}" created`);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to create rule');
        throw err;
      }
    },
    [createRule]
  );

  // Handle update rule
  const handleUpdate = useCallback(
    async (id: number, data: Parameters<typeof updateRule>[1]) => {
      try {
        await updateRule(id, data);
        toast.success('Rule updated');
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to update rule');
        throw err;
      }
    },
    [updateRule]
  );

  // Handle toggle active
  const handleToggleActive = useCallback(
    async (id: number, active: boolean) => {
      try {
        await updateRule(id, { active });
        toast.success(active ? 'Rule enabled' : 'Rule disabled');
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to update rule');
        throw err;
      }
    },
    [updateRule]
  );

  // Handle delete click (show confirmation)
  const handleDeleteClick = useCallback(async (id: number) => {
    const rule = rules.find((r) => r.id === id);
    if (rule) {
      setDeleteConfirm({
        isOpen: true,
        ruleId: id,
        ruleName: rule.name,
      });
    }
  }, [rules]);

  // Handle delete confirm
  const handleDeleteConfirm = useCallback(async () => {
    if (deleteConfirm.ruleId === null) return;

    setIsDeleting(true);
    try {
      await deleteRule(deleteConfirm.ruleId);
      toast.success(`Rule "${deleteConfirm.ruleName}" deleted`);
      setDeleteConfirm({ isOpen: false, ruleId: null, ruleName: '' });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete rule');
    } finally {
      setIsDeleting(false);
    }
  }, [deleteConfirm, deleteRule]);

  // Handle delete cancel
  const handleDeleteCancel = useCallback(() => {
    setDeleteConfirm({ isOpen: false, ruleId: null, ruleName: '' });
  }, []);

  // Handle test pattern
  const handleTestPattern = useCallback(
    async (pattern: string) => {
      const result = await testPattern(pattern);
      return result;
    },
    [testPattern]
  );

  // Handle preview click
  const handlePreviewClick = useCallback(async (rule: ClassificationRule) => {
    setPreviewRule(rule);
    setIsPreviewLoading(true);
    try {
      const data = await fetchPreviewData(rule.id, true);
      setPreviewData({ transactions: data.transactions, rule: data.rule });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to load preview');
      setPreviewRule(null);
    } finally {
      setIsPreviewLoading(false);
    }
  }, [fetchPreviewData]);

  // Handle preview filter change
  const handlePreviewFilterChange = useCallback(async (onlyPending: boolean) => {
    if (!previewRule) return;
    setIsPreviewLoading(true);
    try {
      const data = await fetchPreviewData(previewRule.id, onlyPending);
      setPreviewData({ transactions: data.transactions, rule: data.rule });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to load preview');
    } finally {
      setIsPreviewLoading(false);
    }
  }, [previewRule, fetchPreviewData]);

  // Handle preview close
  const handlePreviewClose = useCallback(() => {
    setPreviewRule(null);
    setPreviewData(null);
  }, []);

  // Handle apply rule
  const handleApplyRule = useCallback(async (transactionIds: number[], includeClassified: boolean) => {
    if (!previewRule) return;
    try {
      const result = await applyRuleApi(previewRule.id, transactionIds, includeClassified);
      toast.success(result.message);
      await fetchRules(); // Refresh rules to update match counts
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to apply rule');
      throw err;
    }
  }, [previewRule, applyRuleApi, fetchRules]);

  // Handle toggle selection for a single rule
  const handleToggleRule = useCallback((id: number) => {
    setSelectedRuleIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // Handle select all rules
  const handleSelectAll = useCallback(() => {
    setSelectedRuleIds(new Set(rules.map((r) => r.id)));
  }, [rules]);

  // Handle deselect all rules
  const handleDeselectAll = useCallback(() => {
    setSelectedRuleIds(new Set());
  }, []);

  // Handle bulk apply rules
  const handleBulkApply = useCallback(async (scope: BulkApplyScope) => {
    if (selectedRuleIds.size === 0) return;

    setIsBulkApplying(true);
    try {
      const result = await bulkApplyRules(Array.from(selectedRuleIds), scope, false);
      if (result.success && result.message) {
        toast.success(result.message);
      } else if (result.success) {
        toast.success(`Applied ${selectedRuleIds.size} rules to transactions`);
      }
      setSelectedRuleIds(new Set()); // Clear selection after success
      await fetchRules(); // Refresh rules to update match counts
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to apply rules');
    } finally {
      setIsBulkApplying(false);
    }
  }, [selectedRuleIds, bulkApplyRules, fetchRules]);

  // Computed values for selection
  const allRulesSelected = rules.length > 0 && selectedRuleIds.size === rules.length;
  const someRulesSelected = selectedRuleIds.size > 0 && selectedRuleIds.size < rules.length;

  return (
    <>
      <Head title="Classification Rules" />
      <Toaster position="top-right" richColors closeButton />

      <div className="min-h-screen bg-gray-50" data-testid="classification-rules-page">
        <Navbar currentSection="accounting" />

        {/* Header */}
        <header className="bg-white border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link
                href="/accounting/transactions"
                className="inline-flex items-center gap-1.5 px-2 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft size={16} />
                Transactions
              </Link>
              <div>
                <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
                  <ListChecks className="text-blue-500" size={24} />
                  Classification Rules
                </h1>
                <p className="mt-1 text-sm text-gray-500">
                  Manage rules for automatic transaction classification
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleCreateClick}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                data-testid="create-rule-button"
              >
                <Plus size={16} />
                Create Rule
              </button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="px-6 py-6 max-w-4xl mx-auto">
          {/* Error State */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <svg
                className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <div>
                <h3 className="font-semibold text-red-800">Error Loading Rules</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
                <button
                  onClick={() => {
                    clearError();
                    fetchRules();
                  }}
                  className="mt-2 text-sm font-medium text-red-700 hover:text-red-900 underline"
                >
                  Try again
                </button>
              </div>
            </div>
          )}

          {/* Loading State */}
          {loading && rules.length === 0 && (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="bg-white rounded-lg border border-gray-200 overflow-hidden animate-pulse"
                >
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex justify-between">
                    <div className="h-5 bg-gray-200 rounded w-32" />
                    <div className="h-5 bg-gray-200 rounded w-12" />
                  </div>
                  <div className="px-4 py-3 border-b border-gray-100">
                    <div className="h-10 bg-gray-100 rounded" />
                  </div>
                  <div className="px-4 py-3 border-b border-gray-100">
                    <div className="h-4 bg-gray-100 rounded w-48" />
                  </div>
                  <div className="px-4 py-3">
                    <div className="flex gap-2">
                      <div className="flex-1 h-10 bg-gray-200 rounded" />
                      <div className="h-10 bg-gray-200 rounded w-24" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && rules.length === 0 && (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-6 py-16 text-center">
                <ListChecks className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h2 className="text-lg font-semibold text-gray-700 mb-2">
                  No Classification Rules
                </h2>
                <p className="text-sm text-gray-500 max-w-md mx-auto mb-6">
                  Create rules to automatically classify transactions based on
                  patterns in their descriptions.
                </p>
                <button
                  type="button"
                  onClick={handleCreateClick}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  <Plus size={16} />
                  Create Your First Rule
                </button>
              </div>
            </div>
          )}

          {/* Rules List */}
          {!loading && rules.length > 0 && (
            <>
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {/* Select All Checkbox */}
                  <button
                    type="button"
                    onClick={allRulesSelected ? handleDeselectAll : handleSelectAll}
                    className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded"
                    data-testid="select-all-checkbox"
                    aria-label={allRulesSelected ? 'Deselect all rules' : 'Select all rules'}
                  >
                    {allRulesSelected ? (
                      <CheckSquare size={18} className="text-blue-600" />
                    ) : someRulesSelected ? (
                      <CheckSquare size={18} className="text-blue-400" />
                    ) : (
                      <Square size={18} className="text-gray-400" />
                    )}
                  </button>
                  <p className="text-sm text-gray-600">
                    {selectedRuleIds.size > 0
                      ? `${selectedRuleIds.size} of ${rules.length} rule${rules.length !== 1 ? 's' : ''} selected`
                      : `${rules.length} rule${rules.length !== 1 ? 's' : ''}`}
                  </p>
                </div>
                {loading && (
                  <Loader2 size={16} className="animate-spin text-gray-400" />
                )}
              </div>

              <div className="space-y-4" data-testid="classification-rules-list">
                {rules.map((rule) => (
                  <ClassificationRuleCard
                    key={rule.id}
                    rule={rule}
                    onEdit={handleEditClick}
                    onDelete={handleDeleteClick}
                    onToggleActive={handleToggleActive}
                    onPreview={handlePreviewClick}
                    disabled={loading}
                    selected={selectedRuleIds.has(rule.id)}
                    onToggleSelect={handleToggleRule}
                    selectionMode={true}
                  />
                ))}
              </div>
            </>
          )}
        </main>
      </div>

      {/* Create/Edit Modal */}
      <RuleFormModal
        isOpen={showFormModal}
        onClose={handleModalClose}
        onCreate={handleCreate}
        onUpdate={handleUpdate}
        onTestPattern={handleTestPattern}
        rule={editingRule}
        accounts={accounts}
        accountTree={accountTree}
      />

      {/* Preview Modal */}
      {previewRule && previewData && (
        <RulePreviewModal
          rule={previewData.rule}
          isOpen={!!previewRule}
          onClose={handlePreviewClose}
          onApply={handleApplyRule}
          transactions={previewData.transactions}
          isLoading={isPreviewLoading}
          onFilterChange={handlePreviewFilterChange}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm.isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={handleDeleteCancel}
          data-testid="delete-confirm-backdrop"
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6"
            onClick={(e) => e.stopPropagation()}
            data-testid="delete-confirm-modal"
          >
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Delete Rule
                </h3>
                <p className="mt-2 text-sm text-gray-600">
                  Are you sure you want to delete the rule{' '}
                  <strong>"{deleteConfirm.ruleName}"</strong>? This action cannot
                  be undone.
                </p>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={handleDeleteCancel}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="confirm-delete-button"
              >
                {isDeleting ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : null}
                {isDeleting ? 'Deleting...' : 'Delete Rule'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Apply Bar */}
      <BulkApplyBar
        selectedCount={selectedRuleIds.size}
        onApply={handleBulkApply}
        onClearSelection={handleDeselectAll}
        isLoading={isBulkApplying}
      />
    </>
  );
}
